import { useMemo } from 'react'
import './table.css'
import { DuplicateRow, MetaEntry } from '../../lib/types'
import RowIndicator from './row-indicator'
import NoteIndicator from './note-indicator'
import getId, { getCaseInsensitive } from '../../lib/get-id'
import { findRowMeta } from '../../workflows/util'

type Props = {
    data: Array<any>
    meta: Array<MetaEntry>
    onClick: (obj: any, modKey: boolean) => void
    indicatorFn: (id: number, meta: MetaEntry) => string
    fields?: Array<string>
    duplicates?: Array<DuplicateRow>
    isArbitrate?: boolean
}

function render(value: any) {
    if (typeof value === 'boolean')
        return <span className='text-blue-600 uppercase text-sm font-medium'>{String(value)}</span>
    return value
}

export default function Table(props: Props) {
    const { data, meta, indicatorFn, onClick, fields, isArbitrate } = props
    const headers = useMemo(() => {
        if (fields && fields.length) return fields
        if (data.length) return Object.keys(data[0]).filter((h) => h !== '_user_')
        return []
    }, [data, fields])

    if (!data.length)
        return (
            <div className='text-gray-600 my-5 text-sm'>
                <em>Nothing to show</em>
            </div>
        )

    function getMarker(duplicate: DuplicateRow | undefined) {
        if (duplicate) {
            const extras = JSON.parse(duplicate.extras)
            if (extras.markedDuplicateBy) {
                return extras.markedDuplicateBy
            } else {
                return 'unknown'
            }
        } else {
            return 'unknown'
        }
    }

    return (
        <div className='table-holder'>
            <table>
                <thead>
                    <tr>
                        <th className='meta-cell' />
                        <th className='meta-cell'>Last Updated</th>
                        <th className='meta-cell'>Updated By</th>
                        {headers.map((h, i) => (
                            <th key={i}>{h}</th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {data.map((row, i) => {
                        const rowMeta = findRowMeta(meta, row, isArbitrate)
                        const duplicate = props.duplicates?.find((d) => d.objId === getId(row, isArbitrate))

                        if (!rowMeta) {
                            return <></>
                        }
                        const isHighlighted = isArbitrate && (i % 4 === 0 || i % 4 === 1);

                        return (
                            <tr key={i} onClick={(e) => onClick(row, e.ctrlKey || e.metaKey)} className={isHighlighted ? 'bg-blue-300' : ''}>
                                <td className='meta-cell'>
                                    <RowIndicator color={indicatorFn(getId(row, isArbitrate), rowMeta)} />
                                    {duplicate && (
                                        <span className='text-xs text-gray-500'>
                                            {getMarker(duplicate)}
                                        </span>
                                    )}
                                    <NoteIndicator meta={rowMeta} />
                                </td>
                                <td className='meta-cell'>
                                    {new Date(rowMeta.reviewFinished).toLocaleDateString()}
                                </td>
                                <td className='meta-cell'>
                                    {rowMeta.username || ''}
                                </td>
                                {headers.map((h, i) => (
                                    <td key={i}>{render(getCaseInsensitive(row, h))}</td>
                                ))}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
